import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, SearchCheck } from "@/lib/icons";
import { api } from "@/lib/api";

const panelClass = "admin-surface";

const STATUS_META = {
  pass: { icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-400/10", border: "border-emerald-400/25" },
  warn: { icon: AlertTriangle, color: "text-amber-300", bg: "bg-amber-400/10", border: "border-amber-400/25" },
  fail: { icon: XCircle, color: "text-red-300", bg: "bg-red-400/10", border: "border-red-400/25" },
};

function CheckRow({ check }) {
  const meta = STATUS_META[check.status] || STATUS_META.warn;
  const Icon = meta.icon;
  return (
    <div className={`flex items-start gap-3 rounded-xl border ${meta.border} ${meta.bg} px-4 py-3`} data-testid={`seo-check-${check.id}`}>
      <Icon size={16} className={`mt-0.5 shrink-0 ${meta.color}`} />
      <div className="min-w-0">
        <div className="text-sm font-medium text-white/85">{check.label}</div>
        <div className="mt-0.5 text-xs text-white/50">{check.detail}</div>
      </div>
    </div>
  );
}

export default function AdminSeoHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/seo/health").then((r) => setData(r.data)).catch(() => setData({ checks: [], passed: 0, total: 0 })).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const score = data?.total ? Math.round((data.passed / data.total) * 100) : null;

  return (
    <div className="space-y-8">
      <header className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Search visibility</div>
          <h1 className="mt-2 font-display text-4xl text-white sm:text-5xl">SEO Health</h1>
          <p className="admin-page-description">Live technical checks for indexing, metadata, sitemaps and analytics tracking.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="seo-health-refresh"
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Re-check
        </button>
      </header>

      {loading && !data ? (
        <div className="py-16 text-white/50">Running checks…</div>
      ) : (
        <>
          <section className={`${panelClass} p-6 flex items-center gap-5`}>
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#9D00FF]/25 bg-[#9D00FF]/10">
              <SearchCheck size={22} className="text-[#c66bff]" />
            </span>
            <div>
              <div className="font-display text-3xl text-white tabular-nums">
                {score === null ? "-" : `${score}%`}
              </div>
              <div className="text-xs text-white/45">
                {data?.total ? `${data.passed} of ${data.total} checks passing` : "No checks ran yet"}
              </div>
            </div>
          </section>

          <section className={`${panelClass} p-5`}>
            <h3 className="font-display text-lg text-white mb-4">Checks</h3>
            <div className="space-y-2.5">
              {(data?.checks || []).map((check) => <CheckRow key={check.id} check={check} />)}
              {!data?.checks?.length && <p className="py-8 text-center text-sm text-white/35">Nothing to show yet.</p>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
